// iter883 — Recon Review Queue widget.
//
// Lists supplier_payments + partner_ledger_entries created by bank-recon
// smart hooks in a `pending_review` state. Each row has one-click
// Approve / Reject actions that hit the /api/bank-recon/review-queue
// endpoints.
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, X, RefreshCw, ClipboardCheck } from 'lucide-react';

export function ReconReviewQueueCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bank-recon/review-queue');
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get('/bank-accounts').then((r) => setBanks((Array.isArray(r.data) ? r.data : []).filter((a) => a.active !== false))).catch(() => {});
  }, []);
  const [banks, setBanks] = useState([]);
  const [bankPick, setBankPick] = useState({});

  const act = async (id, action) => {
    const item = items.find((x) => x.id === id);
    const dup = action === 'approve' && (item?.possible_duplicates || []).length > 0;
    if (dup && !window.confirm('This payment may duplicate a confirmed payment (same supplier and amount within 5 days). Approve anyway?')) return;
    setBusy((b) => ({ ...b, [id]: action }));
    try {
      const body = { ...(dup ? { acknowledge_duplicate: true } : {}), ...(bankPick[id] ? { bank_account_id: bankPick[id] } : {}) };
      const { data } = await api.post(`/bank-recon/review-queue/${id}/${action}`, Object.keys(body).length ? body : undefined);
      toast.success(action === 'approve' ? `Approved${data?.journal_status ? ` · journal ${data.journal_status}` : ''}` : 'Rejected');
      setItems((it) => it.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setBusy((b) => {
        const nb = { ...b };
        delete nb[id];
        return nb;
      });
    }
  };

  if (loading) {
    return (
      <Card data-testid="recon-review-queue-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck size={16} />
            Recon Review Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="recon-review-queue-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardCheck size={16} className="text-indigo-600" />
          Recon Review Queue
          {items.length > 0 && (
            <Badge variant="secondary" data-testid="review-queue-count">
              {items.length}
            </Badge>
          )}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          className="h-7 w-7 p-0"
          title="Refresh"
          data-testid="review-queue-refresh"
        >
          <RefreshCw size={13} />
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-6" data-testid="review-queue-empty">
            <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
            <p className="text-sm text-muted-foreground">
              All caught up — no drafts pending review
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {items.map((it) => (
              <div
                key={it.id}
                className="rounded-lg border border-border p-3 bg-background/50 flex items-start gap-3"
                data-testid={`review-item-${it.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge
                      variant="outline"
                      className="text-[10px] font-normal capitalize"
                    >
                      {it.kind === 'supplier_payment'
                        ? 'Supplier'
                        : it.kind === 'partner_ledger_entry'
                        ? `Partner ${it.entry_type || ''}`
                        : it.kind}
                    </Badge>
                    <span
                      className="text-sm font-semibold truncate"
                      data-testid={`review-item-name-${it.id}`}
                    >
                      {it.party_name || '(unknown)'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span
                      className="font-mono font-medium text-foreground"
                      data-testid={`review-item-amount-${it.id}`}
                    >
                      {cur(it.amount || 0)}
                    </span>
                    <span>{it.date}</span>
                  </div>
                  {it.notes && (
                    <p className="text-[11px] text-muted-foreground/80 mt-1 truncate">
                      {it.notes}
                    </p>
                  )}
                  {it.kind === 'supplier_payment' && (
                    <div className="text-[11px] mt-1 space-y-0.5" data-testid={`review-evidence-${it.id}`}>
                      <div data-testid={`review-journal-status-${it.id}`}>Journal: {it.journal_status || 'not_posted'} · Bank evidence: {it.bank_statement_line ? `statement line ${it.bank_statement_line.date || ''} ${cur(it.bank_statement_line.debit || it.bank_statement_line.credit || 0)}` : 'none'}</div>
                      {(it.possible_duplicates || []).length > 0 && (
                        <div className="text-amber-700 font-medium" data-testid={`review-duplicate-warning-${it.id}`}>
                          Possible duplicate: {it.possible_duplicates.map((d) => `${d.date} ${cur(d.amount)} (${d.payment_mode || '—'}, journal ${d.journal_entry_id ? 'posted' : 'none'})`).join('; ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {it.kind === 'supplier_payment' && banks.length > 0 && (
                    <select className="h-7 rounded border bg-background px-1 text-[11px]" value={bankPick[it.id] || ''} onChange={(e) => setBankPick((p) => ({ ...p, [it.id]: e.target.value }))} data-testid={`review-bank-${it.id}`}>
                      <option value="">Paid from bank…</option>
                      {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    disabled={!!busy[it.id] || !!it.reversed_at}
                    title={it.reversed_at ? 'Reversed — cannot be approved' : undefined}
                    onClick={() => act(it.id, 'approve')}
                    data-testid={`review-approve-${it.id}`}
                  >
                    <CheckCircle2 size={12} /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                    disabled={!!busy[it.id]}
                    onClick={() => act(it.id, 'reject')}
                    data-testid={`review-reject-${it.id}`}
                  >
                    <X size={12} /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReconReviewQueueCard;
